import { Box } from 'components/ui/Box'
import { Typography } from 'components/ui/Typography'
import { StaticContent as IStaticContent, StaticContentItem } from 'interfaces/StaticContent'
import { FC } from 'react'
import { DefaultTheme, useTheme } from 'styled-components'
import uniqid from 'uniqid'

import { ContentWrapper, FloatedTypography, Paragraph } from './styles'

interface Props {
  content: Array<IStaticContent>
}

const renderHTMLContent = (text: string, html = false) => {
  if (html) return <span dangerouslySetInnerHTML={{ __html: text }} />
  return text
}

export const renderContentItem = (
  theme: DefaultTheme,
  contentItem: string | StaticContentItem,
  index: string | number,
  contentType: 'dots' | 'numbers' | 'none',
  indent = true,
  useHTML = false
) => {
  if (typeof contentItem === 'string')
    return (
      <Box key={index} marginTop={indent ? 16 : 0} marginLeft={contentType === 'dots' ? 8 : 0}>
        {contentType === 'numbers' && (
          <FloatedTypography typography={theme.typography.variants.content.BOLD}>
            {index}
          </FloatedTypography>
        )}
        {contentType === 'dots' && (
          <Box height={19} display='inline-block'>
            <FloatedTypography dots typography={theme.typography.variants.title.H1}>
              {String.fromCharCode(183)}
            </FloatedTypography>
          </Box>
        )}
        &nbsp;
        <Typography>{renderHTMLContent(contentItem, useHTML)}</Typography>
      </Box>
    )

  return (
    <Box key={index} marginTop={indent ? 16 : 0} marginLeft={contentType === 'dots' ? 8 : 0}>
      {contentType === 'numbers' && (
        <Typography typography={theme.typography.variants.content.BOLD}>{index}&nbsp;</Typography>
      )}
      {contentType === 'dots' && (
        <Box height={19} display='inline-block'>
          <FloatedTypography dots typography={theme.typography.variants.title.H1}>
            {String.fromCharCode(183)}
          </FloatedTypography>
        </Box>
      )}
      &nbsp;
      <Typography
        typography={
          contentItem.bold
            ? theme.typography.variants.content.BOLD
            : theme.typography.variants.content.MAIN
        }
      >
        {contentItem.description}
      </Typography>
      <Box>
        {typeof contentItem.content === 'string' && (
          <Typography>{renderHTMLContent(contentItem.content, contentItem.useHTML)}</Typography>
        )}
        {Array.isArray(contentItem.content) &&
          contentItem.content.map((additionalContent, idx) =>
            renderContentItem(
              theme,
              additionalContent,
              `${index}.${idx + 1}`,
              contentItem.contentType,
              false
            )
          )}
      </Box>
    </Box>
  )
}

export const StaticContent: FC<Props> = ({ content }) => {
  const theme = useTheme()

  return (
    <ContentWrapper>
      <Box marginTop={24}>
        {content.map((offerItem, i) => (
          <Paragraph key={uniqid()}>
            <Typography typography={theme.typography.variants.title.H2}>
              {i + 1}. {offerItem.title}
            </Typography>
            <Paragraph level='second'>
              {offerItem.content && typeof offerItem.content === 'string' && (
                <Typography>{renderHTMLContent(offerItem.content, offerItem.useHTML)}</Typography>
              )}
              {offerItem.content &&
                Array.isArray(offerItem.content) &&
                offerItem.content.map((contentItem, k) =>
                  renderContentItem(
                    theme,
                    contentItem,
                    `${i + 1}.${k + 1}`,
                    'numbers',
                    true,
                    offerItem.useHTML
                  )
                )}
              {offerItem.terms &&
                offerItem.terms.map(term => (
                  <Paragraph key={uniqid()} level='second'>
                    {!term.name ? (
                      <Typography>{term.description}</Typography>
                    ) : (
                      <>
                        <Typography typography={theme.typography.variants.content.BOLD}>
                          {term.name}
                        </Typography>
                        &nbsp; - <Typography>{term.description}</Typography>
                      </>
                    )}
                  </Paragraph>
                ))}
            </Paragraph>
          </Paragraph>
        ))}
      </Box>
    </ContentWrapper>
  )
}
